import React, { useState } from 'react';
import { Box, Modal, IconButton } from '@mui/material';
import { Close as CloseIcon, ZoomIn as ZoomInIcon, PlayCircleOutline } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const MotionBox = motion(Box);

// Control whether to allow dummy images (set to false to disable later)
// const ALLOW_DUMMY_IMAGES = true;

const getCleanExtension = (url) => {
  try {
    const pathname = new URL(url).pathname;
    return pathname.split('.').pop()?.toLowerCase();
  } catch {
    return '';
  }
};

// Match common image URLs including services like picsum.photos (even without file extension)
const isLikelyImageUrl = (url) =>
  /(picsum\.photos|static\.photos|unsplash\.com|placekitten\.com|dummyimage\.com|via\.placeholder\.com)/i.test(url);

// Check if it’s a valid image
const isImage = (url) => {
  const ext = getCleanExtension(url);
  return (
    ['jpeg', 'jpg', 'gif', 'png', 'webp', 'bmp', 'svg'].includes(ext) ||
    isLikelyImageUrl(url)
  );
};

// Check if it’s a video
const isVideo = (url) => ['mp4', 'webm', 'ogg'].includes(getCleanExtension(url));


// const isDummyImage = (url) => ALLOW_DUMMY_IMAGES && url.includes('.com'|| '.photos');
// const isVideo = (url) => ['mp4', 'webm', 'ogg'].includes(getCleanExtension(url));
// const isImage = (url) =>
//   ['jpeg', 'jpg', 'gif', 'png', 'webp', 'bmp', 'svg'].includes(getCleanExtension(url)) || isDummyImage(url);

const MediaPreview = ({
  src,
  alt = 'Media Alt Text',
  previewHeight = { xs: 160, sm: 200, md: 220 },
  previewWidth = { xs: '100%', sm: 320, md: 360 },
  style = {},
  ...props
}) => {
    // console.log(src)
  const [open, setOpen] = useState(false);
  // const theme = useTheme();

  if (!src) return null;

  const renderPreview = () => {
    if (isVideo(src)) {
      return (
        <MotionBox
          whileHover={{ scale: 1.03, boxShadow: '0 8px 32px rgba(38,166,154,0.18)' }}
          transition={{ type: 'spring', stiffness: 300, damping: 22 }}
          sx={{
            position: 'relative',
            width: previewWidth,
            height: previewHeight,
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: 3,
            cursor: 'pointer',
            bgcolor: 'background.paper',
            ...style,
          }}
        >
          <video
            src={src}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: 'brightness(0.7)',
              display: 'block',
            }}
            muted
            loop
            playsInline
            preload="metadata"
            onClick={() => setOpen(true)}
          />
          <IconButton
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.4)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              fontSize: 48,
              zIndex: 2,
            }}
            onClick={() => setOpen(true)}
            aria-label="Preview Video"
          >
            <PlayCircleOutline sx={{ fontSize: 54 }} />
          </IconButton>
        </MotionBox>
      );
    }

    if (isImage(src)) {
      return (
        <MotionBox
          whileHover={{ scale: 1.03, boxShadow: '0 8px 32px rgba(38,166,154,0.18)' }}
          transition={{ type: 'spring', stiffness: 300, damping: 22 }}
          sx={{
            width: previewWidth,
            height: previewHeight,
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: 3,
            cursor: 'pointer',
            position: 'relative',
            bgcolor: 'background.paper',
            ...style,
          }}
          onClick={() => setOpen(true)}
        >
          <motion.img
            src={src}
            alt={alt}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
            initial={{ scale: 1 }}
            whileHover={{ scale: 1.04 }}
            transition={{ type: 'spring', stiffness: 300, damping: 22 }}
            loading="lazy"
          />
          <IconButton
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.3)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              zIndex: 2,
            }}
            onClick={() => setOpen(true)}
            aria-label="Zoom Image"
          >
            <ZoomInIcon />
          </IconButton>
        </MotionBox>
      );
    }

    return <Box>Unsupported media</Box>;
  };

  const renderFullScreen = () => (
    <AnimatePresence>
      {open && (
        <Modal
          open={open}
          onClose={() => setOpen(false)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
          BackdropProps={{ style: { background: 'rgba(0,0,0,0.92)' } }}
        >
          <MotionBox
            key="fullscreen-media"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 260, damping: 24 }}
            sx={{
              position: 'relative',
              maxWidth: { xs: '98vw', sm: '90vw', md: '80vw' },
              maxHeight: { xs: '90vh', sm: '90vh', md: '90vh' },
              outline: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'transparent',
            }}
          >
            <IconButton
              onClick={() => setOpen(false)}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                color: '#fff',
                bgcolor: 'rgba(0,0,0,0.5)',
                zIndex: 2,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
              }}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
            {isVideo(src) ? (
              <motion.video
                src={src}
                controls
                autoPlay
                style={{
                  maxWidth: '100vw',
                  maxHeight: '80vh',
                  borderRadius: 12,
                  background: '#000',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                }}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 40 }}
                transition={{ duration: 0.3 }}
              />
            ) : (
              <motion.img
                src={src}
                alt={alt}
                style={{
                  maxWidth: '100vw',
                  maxHeight: '80vh',
                  borderRadius: 12,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                  background: '#222',
                }}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 40 }}
                transition={{ duration: 0.3 }}
              />
            )}
          </MotionBox>
        </Modal>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {renderPreview()}
      {renderFullScreen()}
    </>
  );
};

export default MediaPreview;
